import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CERTIFICATE_STATUS, CERTIFICATE_TYPES, getCertificateStatusColour } from "../../../constants/certificate-constants";
import { COMMAND_STATE } from "../../../constants/command-status";
import Commands from '../../../constants/commands';
import { HardwareSubCmds } from '../../../constants/sub-commands';
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { ExecuteCommand } from "../../../models/ExecuteCommand";
import { ListQuery } from "../../../models/ListQuery";
import { executeCommand } from "../../../services/executeCommandService";
import { getCertificateDetailsById, getCertificateNamesWithPagination } from "../../../services/securityPolicyService";
import { showError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";
import RefreshButton from "../../fields/buttons/refresh-button";
import Label from "../../label/label";
import PaginatedSearchableSelectField from "../../selection-field/paginated-searchable-select-field";
import { toast } from "react-toastify";
import { ModalType } from "./ModalType";

interface tabProps {
    setModalType: (type: ModalType) => void;
    certToUploadStatus: { status: string, message: string };
    setCertToUploadStatus: (status: { status: string, message: string }) => void;
    reloadPage?: () => void;
}

const CertificateTab: FC<tabProps> = ({ setModalType, certToUploadStatus, setCertToUploadStatus, reloadPage }) => {
    const { translate } = useLocale();
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const { values, setFieldValue } = useFormikContext<any>();
    const [certificatesDetails, setCertificatesDetails] = useState<any[] | null>(null);
    const [certificateToUploadDetails, setCertificateToUploadDetails] = useState<any | null>(null);

    useEffect(() => {
        if (values.certificates && values.certificates.length > 0) {
            // get all certificates
            const certificates = values.certificates;

            // Sort certificates to have the active one first
            const sortedCertificates = getSortedCertificates(certificates);

            // fetch the details of each certificate
            loadCertificatesDetails(sortedCertificates);
        } else {
            setCertificatesDetails(null);
        }
    }, [values.certificates]);

    useEffect(() => {
        loadCertificateDetails();
    }, [values.ksCertificateToUploadId]);

    const getSortedCertificates = (certificates) => {
        return certificates.sort((a, b) => {
            if (a.ksCertificateStatusId === CERTIFICATE_STATUS.ACTIVE && b.ksCertificateStatusId !== CERTIFICATE_STATUS.ACTIVE) {
                return -1; // a comes first
            } else if (a.ksCertificateStatusId !== CERTIFICATE_STATUS.ACTIVE && b.ksCertificateStatusId === CERTIFICATE_STATUS.ACTIVE) {
                return 1; // b comes first
            }
            return 0; // maintain original order for other statuses
        });
    }

    const loadCertificatesDetails = async (sortedCertificates) => {
        try {
            const certificatePromises = sortedCertificates.map(cert =>
                fetchCertificateDetails(cert.ksCertificateId, cert.ksCertificateStatus)
            );
            const certificatesData = await Promise.all(certificatePromises);
            // Filter out any undefined values (in case some requests failed)
            const validCertificates = certificatesData.filter(cert => cert !== undefined);
            setCertificatesDetails(validCertificates);
        } catch (error) {
            showError(error, toast.warn, translate.byKey(TranslationKeys.gettingCertificateDetailsError));
        }
    };

    const loadCertificateDetails = async () => {
        try {
            const certificatesData = await Promise.resolve(fetchCertificateDetails(values.ksCertificateToUploadId));
            if (certificatesData) {
                setCertificateToUploadDetails(certificatesData);
            } else {
                setCertificateToUploadDetails(null);
            }
        } catch (error) {
            showError(error, toast.warn, translate.byKey(TranslationKeys.gettingCertificateDetailsError));
        }
    };

    const fetchCertificateDetails = async (id: number, status?: string) => {
        if (!id) return;
        try {
            const data = await getCertificateDetailsById(id, translate);
            if (status) {
                data.data.ksCertificateStatus = status;
            }
            return data.data;
        } catch (error) {
            showError(error, toast.warn, translate.byKey(TranslationKeys.gettingCertificateDetailsError));
        }
    };

    const handleCertToUploadChange = (event, item) => {
        setFieldValue('ksCertificateToUploadId', item?.value);
        setFieldValue('ksCertificateToUploadName', item?.label);
        setCertToUploadStatus({ status: '', message: '' });
    };

    const handleDeleteSelectedCertificate = () => {
        setFieldValue('ksCertificateToUploadId', undefined);
        setFieldValue('ksCertificateToUploadName', undefined);
        setCertificateToUploadDetails(null);
        setCertToUploadStatus({ status: '', message: '' });
    }

    const renderCertificateDetails = (certificateDetails) => {
        if (certificateDetails) {
            return (
                <div className="flex flex-col">
                    <div className="w-1/4 grid grid-cols-2 gap-x-2 text-xs">
                        <p className="font-bold">{translate.byKey(TranslationKeys.commonName)}:</p>
                        <p>{certificateDetails.commonName}</p>

                        <p className="font-bold">{translate.byKey(TranslationKeys.issuedOn)}:</p>
                        <p>{certificateDetails.issuedOn}</p>

                        <p className="font-bold">{translate.byKey(TranslationKeys.expiresOn)}:</p>
                        <p>{certificateDetails.expiresOn}</p>

                        <p className="font-bold">{translate.byKey(TranslationKeys.algorithm)}:</p>
                        <p>{certificateDetails.certificateFingerprintAlgorithm}</p>
                    </div>
                    <span
                        onClick={() => {
                            setModalType("details")
                            setFieldValue('ksCertificateDetailsId', certificateDetails.id);
                        }}
                        className="text-xs text-blue-600 mt-1 underline cursor-pointer">{translate.byKey(TranslationKeys.showMore)}</span>
                </div>
            );
        }
    };

    const refreshCertificates = async () => {
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Device.label,
                cmdSubType: HardwareSubCmds.Certificate_Refresh,
                entityId: values.controllerId,
                controllerId: values.controllerId,
            }
            const response = await executeCommand(command);
            if (response && reloadPage) reloadPage();
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    return <div className="flex flex-col flex-1">
        <div className="mb-3 w-full border-b border-gray-400" />
        <div className="flex flex-col space-y-4">
            <div className="flex flex-col flex-1 pr-3">
                {/* Placeholder to give extra empty column */}
            </div>
            <div>
                <div className='flex flex-row justify-between items-center mr-4'>
                    <Label htmlFor='certificateId' className="text-portal-blue-primary text-xl font-bold bound-title">
                        {translate.byKey(TranslationKeys.controllerCertificates)}
                    </Label>
                    <RefreshButton onRefresh={refreshCertificates} />
                </div>
                <div className="flex flex-row">
                    {certificatesDetails?.map((cert, index) => (
                        <div key={index} className="space-y-2 mt-2 flex-1">
                            <Label htmlFor={`certificateId-${index}`} className="text-portal-blue-primary text-md font-bold bound-title">
                                {translate.byKey(TranslationKeys.certificate)}-{index + 1}
                                {cert.ksCertificateStatus &&
                                    <span className={`ml-1 ${getCertificateStatusColour(cert.ksCertificateStatus)}`}>
                                        {cert.ksCertificateStatus ? `(${cert.ksCertificateStatus})` : ''}
                                    </span>
                                }
                            </Label>
                            {cert ? renderCertificateDetails(cert) : <p className="text-xs text-gray-500">{translate.byKey(TranslationKeys.noCertificateInUseYet)}</p>}
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex flex-col flex-1 space-y-2">
                <Label htmlFor='updateCertificate' className="text-portal-blue-primary text-xl font-bold bound-title">
                    {translate.byKey(TranslationKeys.updateCertificate)}
                </Label>
                <div className="flex flex-col">
                    <Label htmlFor='commonName' required className="text-xs text-gray-600">
                        {translate.byKey(TranslationKeys.commonName)}:
                    </Label>
                    <div className="flex flex-row space-x-2 mb-1">
                        <div className="w-64">
                            <PaginatedSearchableSelectField
                            name={translate.byKey(TranslationKeys.certificate)}
                            key={"certificateId"}
                            accessor='ksCertificateToUploadId'
                            // Filter to show only certificates that are not active or assigned already as well as only show controller type.
                            dataMethod={(listParameters?: ListQuery) => getCertificateNamesWithPagination({ ...listParameters,
                                filters: appendFilter(values.siteId ?? siteId, 'Site id', 'site.id',
                                    appendFilter([CERTIFICATE_STATUS.UNKNOWN, CERTIFICATE_STATUS.FAILED], 'Certificate Status', 'ksCertificateStatus.id',
                                        appendFilter(CERTIFICATE_TYPES.CONTROLLER_MQTT,'Certificate Type', 'ksCertificateType.id',
                                            appendFilter(CERTIFICATE_STATUS.REVOKED,'Certificate Status', 'ksCertificateStatus.id',[], "=out="))
                                ))
                            })}
                            searchAccessor={'name'}
                            onSelect={(event, item) => { handleCertToUploadChange(event, item); }}
                            selectedItemLabel={values.ksCertificateToUploadName}
                            onDelete={ handleDeleteSelectedCertificate }/>
                        </div>
                        <button type="button"
                                className={`secondary text-xs py-1 px-5 ${certificateToUploadDetails ? 'secondary-disabled cursor-not-allowed' : 'secondary-enabled'}`}
                                onClick={() => setModalType("generateAndUpload")}
                                disabled={certificateToUploadDetails}>
                            {translate.byKey(TranslationKeys.generateAndUploadCertificate)}
                        </button>
                        <button type="button" className="secondary secondary-enabled text-xs py-1 px-5" onClick={() => setModalType("import")}>
                            {translate.byKey(TranslationKeys.importCertificate)}
                        </button>
                    </div>
                    {certificateToUploadDetails &&
                        <Label htmlFor="certificateToUploadDetails" className="text-portal-blue-primary text-md font-bold bound-title">
                            {translate.byKey(TranslationKeys.selectedCertificate)}
                            {/* Show the status of the certificate being uploaded */}
                            <span className={`ml-1 ${getCertificateStatusColour(certToUploadStatus.status)}`}>
                                {certToUploadStatus.status ? `(${certToUploadStatus.status})` : ''}
                            </span>
                        </Label>
                    }
                    {renderCertificateDetails(certificateToUploadDetails)}
                    {/* Show the error of the certificate being uploaded if there is one */}
                    {certToUploadStatus.status && certToUploadStatus.status === COMMAND_STATE.COMMAND_FAILED &&
                        <div className='text-xs text-red-600'>
                            {certToUploadStatus.message}
                        </div>
                    }
                    {values.ksCertificateToUploadName &&
                        <button type="button" className="rounded-full text-white text-xs py-1 px-5 bg-lime w-max mb-3 mt-3" onClick={() => setModalType("uploadCertConfirm")}>
                            {translate.byKey(TranslationKeys.uploadCertificate)}
                        </button>
                    }
                </div>
            </div>
        </div>
    </div>
};

export default CertificateTab;
